import { handleLoginSubmit } from '@porterin/booking-flow/src/auth/domain/helpers/LoginHelper';
import { handleOtpSubmit } from '@porterin/booking-flow/src/auth/domain/helpers/OtpStateHelper';
import { LoginFormValues } from '@porterin/booking-flow/src/auth/domain/validations/LoginValidationSchema';
import { SignupFormValues } from '@porterin/booking-flow/src/auth/domain/validations/SignupValidationSchema';
import LoginModal from '@porterin/booking-flow/src/auth/view/components/login-modal';
import OtpModal from '@porterin/booking-flow/src/auth/view/components/otp-modal';
import SignupModal from '@porterin/booking-flow/src/auth/view/components/signup-modal';
import { GaEvents } from '@porterin/booking-flow/src/auth/view/stores/AuthAnalyticsStore';
import { PrefillData } from '@porterin/booking-flow/src/auth/view/types/SignupModal';
import {
  AuthContextType,
  getDefaultValue,
  useAuthContext,
  User,
} from '@porterin/booking-flow/src/order-review/view/contexts/auth/AuthContext';
import { AuthProvider } from '@porterin/booking-flow/src/order-review/view/contexts/auth/AuthProvider';
import { mapPageToCategory } from '@porterin/booking-flow/src/utils/BookingFlowAnalyticsHelper';
import Modal from '@porterin/component-library/src/base-components/modal/Modal';
import { publishEvent } from '@porterin/component-library/src/ts-utils/analytics/react-ga/EventTracker';
import { isDesktop } from '@porterin/lead-flow/src/components/FareEstimateHelper';
import { useCallback, useState } from 'react';
import { AuthModalConfig } from '../../domain/types/AuthModalConfig';
import {
  ConsentForm,
  ConsentState,
} from '@porterin/component-library/src/feature-components/consent-modal/ConsentForm';
import { authRepo } from '../../domain/repo/authRepo';
import { useLocaleContext } from '@porterin/component-library/src/contexts/locale/LocaleContext';
import { getConsentTerms } from '../helpers/AuthVmHelper';
import { publishAuthCtaTapEvents } from '../helpers/AuthAnalyticsHelper';
import { formatName } from '@porterin/booking-flow/src/common/domain/helpers/CommonBookingFlowHelpers';
import { AuthContentProvider } from '@porterin/booking-flow/src/auth/view/contexts/auth-content/AuthContentProvider';
import {
  AuthContentVM,
  DEFAULT_AUTH_CONTENT,
} from '@porterin/booking-flow/src/auth/view-model/auth-content/AuthContentVM';

export type AuthModalVariant = 'login' | 'signup' | 'otp' | 'tnc-consent';
interface AuthModalProps {
  isOpen: boolean;
  variant?: AuthModalVariant;
  onClose: () => void;
  config: AuthModalConfig;
  content?: AuthContentVM;
  prefillData?: PrefillData;
  authContext?: AuthContextType;
  onOtpValid?: (authenticatedUser?: User) => void;
  modalCloseTimeout?: number;
  lockMobile?: boolean;
  showChangeMobile?: boolean;
  closeOnSuccess?: boolean;
}

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  variant = 'signup',
  onClose,
  prefillData,
  authContext,
  onOtpValid,
  modalCloseTimeout,
  config,
  content = DEFAULT_AUTH_CONTENT,
  lockMobile = false,
  showChangeMobile = true,
  closeOnSuccess = true,
}: AuthModalProps) => {
  const authContextHook = useAuthContext();
  const authContextValue = authContext ?? authContextHook;

  const [currentAuthState, setCurrentAuthState] =
    useState<AuthModalVariant>(variant);

  const { configs } = useLocaleContext();
  const [signupFormData, setSignupFormData] = useState<SignupFormValues>({
    phone: '',
    name: '',
    email: '',
    whatsappConsent: true,
  });

  const formattedPrefillData = prefillData
    ? { ...prefillData, name: formatName(prefillData?.name ?? '') }
    : null;

  const [prefill, setPrefill] = useState<PrefillData | null>(
    () => formattedPrefillData,
  );

  const [user, setUser] = useState<User | null>(null);

  const close_modal = () => {
    publishEvent({
      category: mapPageToCategory(window.location.pathname),
      action: GaEvents.closeModal,
    });
    onClose();
  };

  const getIdentifiers = () => {
    switch (currentAuthState) {
      case 'login':
        return { id: 'auth-login' };
      case 'signup':
        return { id: 'auth-signup' };
      case 'otp':
        return { id: 'auth-otp' };
      case 'tnc-consent':
        return { id: 'auth-tnc-consent' };
    }
  };

  const proceedToOtp = async (data: SignupFormValues) => {
    await authRepo.customerSignUp('', data.phone, data.email, data.name);
    setUser(prev => ({
      ...prev,
      mobile: data.phone,
      name: prefill?.name ?? data.name,
      requirementType: prefill?.requirement,
      email: data.email,
      whatsappConsent: data.whatsappConsent,
    }));
    setCurrentAuthState('otp');
  };

  const onSignupFormSubmit = async (data: SignupFormValues) => {
    if (configs?.isSignupConsentRequired ?? false) {
      setSignupFormData(data);
      setCurrentAuthState('tnc-consent');
    } else {
      await proceedToOtp(data);
    }
  };

  const onOtpFormSubmit = async (
    otp: string,
    set_error: (message: string) => void,
  ) => {
    handleOtpSubmit(
      otp,
      setUser,
      set_error,
      closeOnSuccess ? close_modal : (): void => undefined,
      user,
      onOtpValid,
      modalCloseTimeout,
      authContextValue?.agreedDocs,
    );
  };

  const onLoginFormSubmit = async (
    data: LoginFormValues,
    is_mobile_updated: boolean,
    set_error: (error: string) => void,
  ) => {
    handleLoginSubmit(
      data,
      is_mobile_updated,
      prefill,
      setPrefill,
      setCurrentAuthState,
      setUser,
      set_error,
    );
  };

  const onChangeMobile = () => {
    setCurrentAuthState('login');
  };

  const getModalComponent = useCallback(() => {
    switch (currentAuthState) {
      case 'login':
        return (
          <LoginModal
            onFormSubmit={onLoginFormSubmit}
            closeModal={close_modal}
            prefill_data={prefill}
            config={config}
            lock_mobile={lockMobile}
          />
        );
      case 'signup':
        return (
          <SignupModal
            onFormSubmit={onSignupFormSubmit}
            prefillData={prefill}
            closeModal={close_modal}
            config={config}
          />
        );
      case 'otp':
        return (
          <OtpModal
            onFormSubmit={onOtpFormSubmit}
            user={user}
            onChangeMobile={onChangeMobile}
            closeModal={close_modal}
            config={config}
            show_change_mobile={showChangeMobile}
          />
        );
      case 'tnc-consent':
        if (!content.tncConsentModal) return null;
        return (
          <ConsentForm
            onClose={close_modal}
            onConfirm={async (consents: ConsentState[]) => {
              publishAuthCtaTapEvents(
                mapPageToCategory(window.location.pathname),
                GaEvents.ctaTap['tnc-consent'],
                'tnc-consent',
              );
              await proceedToOtp(signupFormData);
            }}
            id="auth-flow-tnc-consent-form"
            cmsData={{
              title: content.tncConsentModal.title,
              description: content.tncConsentModal.description,
              agreeAllText: content.tncConsentModal.agreeAllText,
              confirmText: content.tncConsentModal.confirmText,
              closeIcon: {
                src: content.closeIcon.path,
                alt: content.closeIcon.altText,
              },
            }}
            conditions={getConsentTerms(
              content.tncConsentModal.conditions,
              configs.defaultAgreedDocs,
              authContext?.agreedDocs,
            )}
          />
        );
    }
  }, [currentAuthState, prefillData, onSignupFormSubmit]);

  return (
    <Modal
      identifiers={getIdentifiers()}
      modalOptions={{
        isOpen: isOpen,
        showOverlay: true,
        position: isDesktop() ? 'center' : 'bottom-center',
      }}
      interactions={{ onClose: close_modal }}
    >
      <AuthProvider value={authContextValue || getDefaultValue()}>
        <AuthContentProvider value={content}>
          {getModalComponent()}
        </AuthContentProvider>
      </AuthProvider>
    </Modal>
  );
};

export default AuthModal;
